import React, { useState, useEffect } from 'react';
import { Card, Button, Row, Col, Spin, message, Typography, Divider, Result } from 'antd';
import { useLocation, useNavigate } from 'react-router-dom';
import { DollarOutlined, CheckOutlined, StarOutlined, CrownOutlined } from '@ant-design/icons';
import { API_CONFIG, apiClient } from '../api/config';
import { useAuth } from '../user/auth/AuthContext';
import { logger } from '../utils/logger';
import './Prices.css';
import activityTracker from '../user/ActivityTracker';
import usePageTracking from '../utils/usePageTracking';

const { Title, Text, Paragraph } = Typography;

function Prices() {
  // GA4 页面追踪
  usePageTracking('訂閱方案');
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [checkoutLoading, setCheckoutLoading] = useState({});
  const [latestEntitlement, setLatestEntitlement] = useState(null);
  const [cancelling, setCancelling] = useState(false);
  const { user, isAuthenticated } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();

  const searchParams = new URLSearchParams(location.search);
  const isSuccess = searchParams.get('success') === 'true';
  const isCanceled = searchParams.get('canceled') === 'true';

  useEffect(() => {
    activityTracker.trackPageView('prices');
    
    const init = async () => {
      try {
        // 登入才查詢 entitlement
        if (isAuthenticated && user?.user_id) {
          const resp = await apiClient.get(`/api/stripe/entitlements/latest/${user.user_id}`);
          logger.log('獲取最新 entitlements , user id:', user.user_id);
          setLatestEntitlement(resp.data);
        } else {
          setLatestEntitlement(null);
        }

        // 取得 Stripe 後端產品（訂閱性價目）
        try {
          const productsResp = await apiClient.get('/api/stripe/products');
          setProducts(productsResp.data || []);
        } catch (prodErr) {
          logger.error('抓取 Stripe 產品失敗:', prodErr);
        }
      } catch (e) {
        logger.error('取得最新 entitlement 失敗:', e);
        setLatestEntitlement(null);
      } finally {
        setLoading(false);
      }
    };
    init();
  }, [isSuccess, isCanceled, isAuthenticated, user?.user_id]);

  useEffect(() => {
    // 動態載入 Stripe Buy Button 腳本
    const scriptId = 'stripe-buy-button-js';
    if (!document.getElementById(scriptId)) {
      const script = document.createElement('script');
      script.id = scriptId;
      script.async = true;
      script.src = 'https://js.stripe.com/v3/buy-button.js';
      document.body.appendChild(script);
    }
  }, []);

  const fetchProducts = async () => {
    try {
      setLoading(true);
      const response = await apiClient.get('/api/stripe/products');
      setProducts(response.data);
      logger.log('獲取產品列表成功:', response.data);
    } catch (error) {
      logger.error('獲取產品列表失敗:', error);
      message.error('獲取產品列表失敗，請稍後再試');
    } finally {
      setLoading(false);
    }
  };

  const handleCheckout = async (priceId) => {
    if (!isAuthenticated) {
      message.warning('請先登入');
      return;
    }

    try {
      setCheckoutLoading(prev => ({ ...prev, [priceId]: true }));
      
      const response = await apiClient.post('/api/stripe/create-checkout-session', {
        priceId: priceId,
        userId: user.user_id
      });

      if (response.data.url) {
        // 跳轉到Stripe Checkout頁面
        window.location.href = response.data.url;
      } else {
        message.error('無法建立付款會話');
      }
    } catch (error) {
      logger.error('建立付款會話失敗:', error);
      message.error(error.response?.data?.message || '建立付款會話失敗');
    } finally {
      setCheckoutLoading(prev => ({ ...prev, [priceId]: false }));
    }
  };

  const handleCancelSubscription = async () => {
    if (!isAuthenticated || !user?.user_id) {
      message.warning('請先登入');
      return;
    }

    try {
      setCancelling(true);
      
      const response = await apiClient.post(`/api/stripe/cancel-subscription/${user.user_id}`);
      
      if (response.data.success) {
        // 根據目前 entitlement 判斷是否處於試用期（ends_at 在 8 天內且來源是訂閱）
        const now = new Date();
        const endsAtDate = latestEntitlement?.ends_at ? new Date(latestEntitlement.ends_at) : null;
        const msInDay = 24 * 60 * 60 * 1000;
        const withinEightDays = endsAtDate ? ((endsAtDate - now) / msInDay) <= 8 : false;
        const isTrialLike = latestEntitlement?.source === 'subscription' && withinEightDays;

        // 樂觀更新：立即把 cancel_at_end 設為 1，避免等待 webhook 回寫
        setLatestEntitlement(prev => prev ? { ...prev, cancel_at_end: 1 } : prev);

        message.success(isTrialLike
          ? '已取消，試用結束後將不會扣款'
          : '訂閱已設定為週期結束時取消');
        // 重新載入 entitlement 資料
        const resp = await apiClient.get(`/api/stripe/entitlements/latest/${user.user_id}`);
        setLatestEntitlement(resp.data);
        
        // 過三秒後重新整理頁面
        setTimeout(() => {
          window.location.reload();
        }, 3000);
      }
    } catch (error) {
      logger.error('取消訂閱失敗:', error);
      message.error(error.response?.data?.message || '取消訂閱失敗');
    } finally {
      setCancelling(false);
    }
  };

  const formatPrice = (amount, currency = 'TWD') => {
    return new Intl.NumberFormat('zh-TW', {
      style: 'currency',
      currency: currency,
      minimumFractionDigits: 0,
    }).format(amount / 100);
  };

  const getTierIcon = (tier) => {
    switch (tier) {
      case 'free':
        return <StarOutlined style={{ color: '#52c41a' }} />;
      case 'pro':
        return <CrownOutlined style={{ color: '#1890ff' }} />;
      case 'pro+':
        return <CrownOutlined style={{ color: '#faad14' }} />;
      default:
        return <StarOutlined style={{ color: '#52c41a' }} />;
    }
  };

  const getTierColor = (tier) => {
    switch (tier) {
      case 'free':
        return '#52c41a';
      case 'pro':
        return '#1890ff';
      case 'pro+':
        return '#faad14';
      default:
        return '#52c41a';
    }
  };

  const getRecurringIntervalText = (recurring) => {
    if (!recurring) return '一次性';
    
    const { interval, interval_count } = recurring;
    const count = interval_count || 1;
    
    const intervalMap = {
      'day': count === 1 ? '天' : `${count}天`,
      'week': count === 1 ? '週' : `${count}週`,
      'month': count === 1 ? '月' : `${count}月`,
      'year': count === 1 ? '年' : `${count}年`
    };
    
    return intervalMap[interval] || '月';
  };

  if (loading) {
    return (
      <div className="prices-container">
        <div className="loading-container">
          <Spin size="large" />
          <Text>載入中...</Text>
        </div>
      </div>
    );
  }

  // 成功付款頁面
  if (isSuccess) {
    return (
      <div className="prices-container">
        <Result
          status="success"
          title="訂閱成功！"
          subTitle="感謝您的支持，您的訂閱已開通，權限將於短時間內生效。"
          extra={[
            <Button type="primary" key="home" onClick={() => navigate('/', { replace: true })}>
              回到首頁
            </Button>,
            <Button key="keep" onClick={() => navigate('/business-product')}>
              繼續探索功能
            </Button>
          ]}
        />
      </div>
    );
  }

  // 取消付款頁面
  if (isCanceled) {
    return (
      <div className="prices-container">
        <Result
          status="warning"
          title="已取消訂閱"
          subTitle="您可以稍後再試，或回到價格方案重新選擇。"
          extra={[
            <Button type="primary" key="retry" onClick={() => navigate('/prices', { replace: true })}>
              回到價格方案
            </Button>,
            <Button key="home" onClick={() => navigate('/')}>回到首頁</Button>
          ]}
        />
      </div>
    );
  }

  // 判斷顯示狀態
  const today = new Date();
  const entEndsAt = latestEntitlement?.ends_at ? new Date(latestEntitlement.ends_at) : null;
  const isExpired = entEndsAt ? entEndsAt < today : null;

  const renderExpiry = () => (
    <div style={{ maxWidth: 960, margin: '0 auto 12px', textAlign: 'left', color: '#fff'}}>
      <div style={{ marginBottom: 8  }}>
        <Text style={{ color: '#fff' }}>
          訂閱方案：
          <Text strong style={{ color: 'yellow' }}> {isExpired ? 'FREE' : (latestEntitlement?.access_tier ? latestEntitlement.access_tier.toUpperCase() : '無')}</Text>
        </Text>
      </div>
      <div style={{ marginBottom: 8 }}>
        <Text style={{ color: '#fff' }}>
          訂閱日期：
          <Text strong style={{ color: '#fff' }}>{latestEntitlement?.starts_at ? ` ${new Date(latestEntitlement.starts_at).toLocaleString()}` : ' 無'}</Text>
        </Text>
      </div>
      <div style={{ marginBottom: 8 }}>
        <Text style={{ color: '#fff' }}>
          下次訂閱日期 / 訂閱到期日：
          <Text strong style={{ color: '#fff' }}>{entEndsAt ? ` ${new Date(entEndsAt).toLocaleString()}` : ' 無'}</Text>
        </Text>
      </div>
      {latestEntitlement?.cancel_at_end === 1 && (
        <div style={{ marginBottom: 8 }}>
          <Text strong style={{ color: '#ff4d4f', fontSize: '16px' }}>
            已取消訂閱
          </Text>
        </div>
      )}
      {latestEntitlement && !latestEntitlement.cancel_at_end && (
        <div>
          <Button 
            type="primary" 
            danger 
            loading={cancelling}
            onClick={handleCancelSubscription}
            style={{ marginTop: 8 }}
          >
            {cancelling ? '處理中...' : '取消訂閱'}
          </Button>
        </div>
      )}
    </div>
  );

  const renderPlans = () => (
    <div className="prices-container">
      <div className="prices-header">
        <Title level={1} className="prices-title">
          <DollarOutlined /> 價格方案
        </Title>
        <Paragraph className="prices-subtitle">
          選擇適合您的方案，解鎖更多功能
        </Paragraph>


      {/* 原價說明橫向卡片（與上方方案對齊寬度） */}
      <Row gutter={[24, 24]} justify="center" style={{ maxWidth: 960, margin: '16px auto 0' }}>

        <Col xs={24} sm={12} lg={8}>
          <Card hoverable style={{ height: '100%', width: '100%' }}>
            <Title level={4} style={{ marginBottom: 8 }}>原價 Pro $25/月 (美金)</Title>
            <Paragraph style={{ marginBottom: 0 }}>「AI分析查詢 100次 進階選股100次 多視角自選股」</Paragraph>
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={8}>
          <Card hoverable style={{ height: '100%', width: '100%' }}>
            <Title level={4} style={{ marginBottom: 8 }}>原價 Pro $32/月 (美金)</Title>
            <Paragraph style={{ marginBottom: 0 }}>「AI分析查詢 100次 進階選股100次 多視角自選股」</Paragraph>
          </Card>
        </Col>
      </Row>
      <br />
      <div style={{ maxWidth: 960, margin: '0 auto' }}>
          <ul style={{ color: '#faad14', fontSize: 16, lineHeight: 1.7, paddingLeft: 12, marginBottom: 0 }}>

              <Text style={{ color: '#ffffff', fontSize: 16 }}>新用戶第一年開放所有AI功能，只要$6/月『限量100名』</Text> 折扣高達 75%

          </ul>
        </div>
      </div>
      <Row gutter={[24, 24]} justify="center">
        

        <Col xs={24} sm={12} lg={8}>
          <Card className="price-card pro" hoverable style={{ height: '100%', border: `2px solid ${getTierColor('pro')}`, borderRadius: '12px', position: 'relative' }}>
            <div className="popular-badge">
              <Text strong style={{ color: '#fff' }}>年繳省75%</Text>
            </div>
            <div className="price-card-header">
              <div className="tier-icon">{getTierIcon('pro')}</div>
              <Title level={3} className="tier-name">年繳方案</Title>
              <div className="tier-badge">
                <Text strong style={{ color: getTierColor('pro'), fontSize: '14px' }}>Pro</Text>
              </div>
              <div className="price-section">
                <Text className="price-amount">$6 美金</Text>
                <Text className="price-period">/月</Text>
              </div>
            </div>
            <Divider />
            <div className="price-features">
              <div className="features-list">
                {['無限制公司查詢', 'AI查詢分析(首年優惠)到100+100次', '條件選股開放100次', '多視角自選股'].map((feature, index) => (
                  <div key={index} className="feature-item">
                    <CheckOutlined className="feature-check" />
                    <Text>{feature}</Text>
                  </div>
                ))}
              </div>
            </div>
            <div className="price-card-footer">
              <div style={{ textAlign: 'center', marginBottom: 8 }}>
                <Text strong style={{ color: '#52c41a' }}>第一年75%Off 往後每年56%Off</Text>
              </div>
            
            </div>
          </Card>
        </Col>

        {/* 新增 Pro+ 方案卡片 */}
        <Col xs={24} sm={12} lg={8}>
          <Card className="price-card pro-plus" hoverable style={{ height: '100%', border: `2px solid ${getTierColor('pro+')}`, borderRadius: '12px', position: 'relative' }}>
            <div className="popular-badge">
              <Text strong style={{ color: '#fff' }}>每年50%Off</Text>
            </div>
            <div className="price-card-header">
              <div className="tier-icon">{getTierIcon('pro+')}</div>
              <Title level={3} className="tier-name">高級訂閱</Title>
              <div className="tier-badge">
                <Text strong style={{ color: getTierColor('pro+'), fontSize: '14px' }}>Pro+</Text>
              </div>
              <div className="price-section">
                <Text className="price-amount">$16 美金</Text>
                <Text className="price-period">/月</Text>
              </div>
            </div>
            <Divider />
            <div className="price-features">
              <div className="features-list">
                {['包含 Pro 所有功能', '動能翻倍選股功能'].map((feature, index) => (
                  <div key={index} className="feature-item">
                    <CheckOutlined className="feature-check" />
                    <Text>{feature}</Text>
                  </div>
                ))}
              </div>
            </div>
            <div className="price-card-footer">
              <div style={{ textAlign: 'center', marginBottom: 8 }}>
                <Text strong style={{ color: getTierColor('pro+') }}>全新高級產品 Pro+ 現已推出</Text>
                <br />
                <Text strong style={{ color: '#52c41a' }}>每年將漲價2~3次，開幕優惠請盡早升級</Text>
              </div>
            </div>
          </Card>
        </Col>
      </Row>
      
      {/* 後端返回的產品列表（只顯示標題與訂閱按鈕） */}
      {products && products.length > 0 && (
        <div style={{ maxWidth: 960, margin: '16px auto 0' }}>
          <Divider />

          <Row gutter={[24, 24]} justify="center">
            {products.map((p) => (
              <Col xs={24} sm={12} lg={9} key={p.id}>
                <Card size="small" style={{ width: '100%', padding: 12 }}>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12 }}>
                    <Text strong>{p.name}</Text>
                    {!isAuthenticated ? (
                      <Button type="primary" size="large" style={{ minWidth: 180, height: 48, fontSize: 16 }} onClick={() => message.warning('請先登入後再訂閱')}>
                        請先登入再訂閱
                      </Button>
                    ) : (
                      <Button type="primary" size="large" style={{ minWidth: 180, height: 48, fontSize: 16 }} onClick={() => handleCheckout(p?.default_price?.id)}>
                        訂閱
                      </Button>
                    )}
                  </div>
                </Card>
              </Col>
            ))}
          </Row>
        </div>
      )}

      <div className="prices-footer">
        <Title level={4}>常見問題</Title>
        <Row gutter={[24, 16]}>
          <Col xs={24} md={12}>
            <Card size="small" className="faq-card">
              <Title level={5}>訂閱安全嗎？</Title>
              <Text>我們使用Stripe進行安全付款處理，您的付款信息將被加密保護。</Text>
            </Card>
          </Col>
          <Col xs={24} md={12}>
            <Card size="small" className="faq-card">
              <Title level={5}>可以退款嗎？</Title>
              <Text>我們提供7天無條件退款保證，如有任何不滿意可聯繫客服。</Text>
            </Card>
          </Col>
          <Col xs={24} md={12}>
            <Card size="small" className="faq-card">
              <Title level={5}>如何升級方案？</Title>
              <Text>您可以隨時升級到更高級別的方案，差額將按比例計算。訂閱會自動續期。</Text>
            </Card>
          </Col>
          <Col xs={24} md={12}>
            <Card size="small" className="faq-card">
              <Title level={5}>需要客服中心支援？</Title>
              <Text>如果您有任何訂閱上的問題或調整, 直接到"關於我"的填寫客服表單，我們會盡快為您解決問題。</Text>
            </Card>
          </Col>
        </Row>
      </div>
    </div>
  );

  // 三種狀況渲染：
  // 1) 無資料：顯示說明 + 產品列表 + FAQ
  // 2) 有資料且過期：顯示到期日 + 說明 + 產品列表 + FAQ
  // 3) 有資料且未過期：顯示到期日 + FAQ

  if (!latestEntitlement) {
    return renderPlans();
  }

  if (isExpired) {
    return (
      <>
        {renderExpiry()}
        {renderPlans()}
      </>
    );
  }

  return (
    <div className="prices-container">
      <div className="prices-header" style={{ maxWidth: 960, margin: '0 auto' }}>
        {renderExpiry()}
      </div>
      <div className="prices-footer">
        <Title level={4}>常見問題</Title>
        <Row gutter={[24, 16]}>
          <Col xs={24} md={12}>
            <Card size="small" className="faq-card">
              <Title level={5}>訂閱安全嗎？</Title>
              <Text>我們使用Stripe進行安全付款處理，您的付款信息將被加密保護。</Text>
            </Card>
          </Col>
          <Col xs={24} md={12}>
            <Card size="small" className="faq-card">
              <Title level={5}>可以退款嗎？</Title>
              <Text>我們提供14天無條件退款保證，如有任何不滿意可聯繫客服。</Text>
            </Card>
          </Col>
          <Col xs={24} md={12}>
            <Card size="small" className="faq-card">
              <Title level={5}>免費方案有什麼限制？</Title>
              <Text>新帳號前十天開放所有功能, 無任何查詢限制。<br />十天後每個月有20次AI查詢分析, 8次進階選股。</Text>
            </Card>
          </Col>
          <Col xs={24} md={12}>
            <Card size="small" className="faq-card">
              <Title level={5}>需要客服支援？</Title>
              <Text>如果您有任何訂閱上的問題或調整, 直接到"關於我"的填寫客服表單，我們會盡快為您解決問題。</Text>
            </Card>
          </Col>
        </Row>
      </div>
    </div>
  );
}

export default Prices;
